/**
 * useSubscriptionPayment — SOLE network owner for POST subscriptions/pay,
 * the grace payment door.
 *
 * Separate from useMySubscriptions (which owns GET subscriptions/mine) because
 * ownership in this codebase is per-endpoint: one composable, one route.
 *
 * WHAT THIS PAYS. The subscription the franchise ALREADY has — never a new one.
 * The backend resolves the plan, the price row and the gateway from the live
 * subscription row, which is why the only thing sent is franquia_id.
 *
 * THE ENVELOPE RULE (skill billing-staff-console §4 — this bug class has shipped
 * four times). sendError() answers HTTP 200 with {success:false, dados:null,
 * error:'…'}, so httpService does NOT throw and validateResponse() does NOT run
 * (it only validates dados when success === true). A composable that reads
 * `dados` and reports success turns every refusal — 'sem_assinatura_viva',
 * 'assinatura_isenta', 'periodo_corrente_pago' — into a celebration, and then
 * redirects the user to `undefined`. payNow() therefore opts into the
 * httpService envelope guard (`throwOnEnvelopeFailure`, R6'), which rejects
 * with the backend's named reason before `dados` is ever read.
 *
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { isExternalCheckoutUrl } from '../utils/checkoutUrl'
import type { Subscriptions_PayResponseDados } from '../contracts/Subscriptions_PayResponse'

// Side-effect imports: register the contracts with the validation registries.
import '../contracts/Subscriptions_PayInput'
import '../contracts/Subscriptions_PayResponse'

// ── Module-scope singleton state ────────────────────────────────────────────

/** franquia_id currently being paid — drives the row's busy state. */
const payingFranquiaId = ref<number | null>(null)

/** Last refusal / failure reason, already unwrapped from the envelope. */
const payError = ref<string | null>(null)

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscriptionPayment() {
  /**
   * redirectToCheckout — thin, spy-able seam for the external checkout redirect.
   * Mirrors useSubscribe/useCreditTopup so the .vue layer stays free of window.*.
   */
  function redirectToCheckout(url: string): void {
    log('useSubscriptionPayment', 'redirectToCheckout', 'Redirecionando para checkout', { url })
    // New tab, always: the SPA stays alive and the user never needs the
    // browser back button to return from the gateway (2026-08-20).
    window.open(url, '_blank', 'noopener')
  }

  /**
   * payNow — POST subscriptions/pay.
   *
   * Returns dados on success; THROWS the backend's named reason on a refusal
   * (read from the envelope, not from dados) and on transport errors.
   */
  async function payNow(franquiaId: number): Promise<Subscriptions_PayResponseDados> {
    payingFranquiaId.value = franquiaId
    payError.value = null

    try {
      const raw = await httpService.post(
        `${config.api.sistemaHost}/subscriptions/pay`,
        { franquia_id: franquiaId },
        {
          inputContract: 'Subscriptions_PayInput',
          responseContract: 'Subscriptions_PayResponse',
          skipErrorDisplay: false,
          // THE ENVELOPE, not dados — see the header note.
          throwOnEnvelopeFailure: 'pagamento_recusado',
        },
      )

      const resp = raw as { dados: Subscriptions_PayResponseDados }

      log('useSubscriptionPayment', 'payNow', 'Cobrança pronta para pagamento', {
        franquia_id: franquiaId,
        subscription_id: resp.dados?.subscription_id,
        amount_cents: resp.dados?.amount_cents,
        currency: resp.dados?.currency,
        reused: resp.dados?.reused,
      })

      return resp.dados
    } catch (err) {
      payError.value = (err as Error)?.message ?? 'pagamento_recusado'
      log('useSubscriptionPayment', 'payNow', 'Erro ao gerar cobrança', {
        franquia_id: franquiaId,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      payingFranquiaId.value = null
    }
  }

  /**
   * payAndCheckout — payNow() plus the redirect decision.
   *
   * `redirected` tells the caller which half happened:
   *   - true  → the checkout_url is a REAL external payment page and the browser
   *             is already navigating away.
   *   - false → the URL points back at our own API (GatewayTest builds it from
   *             the API host). window.location on that URL tears the SPA down
   *             for nothing — S8R2-01, where a hardcoded 'localhost' check
   *             misclassified it the moment the API got a real hostname and
   *             every purchase smoke died at the redirect. The caller stays put
   *             and refreshes the list instead.
   *
   * The decision lives here, not in the .vue, so the branch is unit-reachable
   * and the component keeps no business logic.
   */
  async function payAndCheckout(
    franquiaId: number,
  ): Promise<{ redirected: boolean; dados: Subscriptions_PayResponseDados }> {
    const dados = await payNow(franquiaId)

    if (dados.checkout_url && isExternalCheckoutUrl(dados.checkout_url)) {
      redirectToCheckout(dados.checkout_url)
      return { redirected: true, dados }
    }

    return { redirected: false, dados }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    payingFranquiaId.value = null
    payError.value = null
  }

  return {
    // reactive state
    payingFranquiaId,
    payError,
    // methods
    payNow,
    payAndCheckout,
    redirectToCheckout,
    _reset,
  }
}
